import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { MediaCollection, ModelType } from "@/gql/graphql";
import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createMediaMutation,
  deleteMediaMutation,
} from "@/app/Media/GraphQL/mediaMutations";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/catalyst/button";
import { ArrowDownTrayIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { useWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignment";

type AssignmentImagesDialogProps = {
  title: string;
  assignmentId: string;
  collection: MediaCollection;
};

export function AssignmentImagesDialog({
  title,
  assignmentId,
  collection,
}: AssignmentImagesDialogProps) {
  const assignment = useWorkOrderAssignment(assignmentId);
  const images =
    collection === MediaCollection.DepartureImage
      ? assignment.departureImages
      : assignment.deliveryImages;

  const queryClient = useQueryClient();
  const { open: openConfirmDelete } = useConfirmDeleteDialog();

  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);
  const { mutateAsync: deleteMedia } = useGqlMutation(deleteMediaMutation);

  function handleDelete(imageId: string) {
    openConfirmDelete({
      title: "Delete Image",
      message: "Are you sure you want to delete this image?",
      onDelete: async () => {
        await deleteMedia({ id: imageId });
        await queryClient.invalidateQueries({
          predicate: (query) =>
            (query.queryKey.at(0) as string)?.includes("WorkOrderAssignment"),
        });
        toast.success("Image deleted");
      },
    });
  }

  return (
    <BaseDialog title={title} size="4xl">
      <div className="space-y-4">
        {images.length > 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {images.map((image) => (
              <div
                key={image.id}
                className="group relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
              >
                <div className="absolute right-2 top-2 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <a
                    href={image.url}
                    download={image.file_name}
                    className="inline-flex items-center justify-center rounded-md bg-white p-1.5 shadow-sm hover:bg-gray-50"
                  >
                    <ArrowDownTrayIcon className="size-4 text-gray-700" />
                  </a>
                  <Button
                    color="red"
                    type="button"
                    onClick={() => handleDelete(image.id)}
                    LeadingIcon={TrashIcon}
                  />
                </div>
                <a href={image.url} target="_blank" rel="noopener noreferrer">
                  <ImoovaImage
                    alt={image.name ?? "Photo"}
                    media={image}
                    className="h-64 w-full object-cover transition-opacity hover:opacity-80"
                  />
                </a>
              </div>
            ))}
          </div>
        ) : null}

        <FileUploader
          maxFileCount={10}
          onUpload={async (uploads) => {
            const results = uploads.map(async (upload) =>
              createMedia({
                input: {
                  file_name: upload.file.name,
                  objectId: assignmentId,
                  objectType: ModelType.WorkOrderAssignment,
                  collectionName: collection,
                  key: upload.filePath,
                },
              }),
            );

            await Promise.allSettled(results);
            await queryClient.invalidateQueries({
              predicate: (query) =>
                (query.queryKey.at(0) as string)?.includes(
                  "WorkOrderAssignment",
                ),
            });
            toast.success("Image uploaded");
          }}
        />
      </div>
    </BaseDialog>
  );
}
